import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../Helpers/Handler";
import { formatDateTime } from "../../Helpers/Utils";
import { useAuth } from "../../Contexts/AuthenticateProvider";
import ListLayout from "../../Components/List";
import useListParams from "../../Helpers/useListParams";
import RingNowDialog from "./RingNowDialog";

const INITIAL_FILTERS = { search: "", searchKey: "taskId", count: 10, status: "", taskType: "", hubId: "", from: "", to: "" };
// The tab rides along in the URL with the filters, but is not a "filter" for the list's empty/reset state.
const LIST_STATE = { ...INITIAL_FILTERS, tab: "mine" };
const SEARCH_KEYS = [{ key: "taskId", value: "Task ID / booking / car / staff" }];

const STATUS_BADGE = {
    pending: "text-bg-secondary",
    assigned: "text-bg-primary",
    progress: "text-bg-warning",
    completed: "text-bg-success",
    cancel: "text-bg-danger",
};

const TaskList = () => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const canViewAll = hasPermission("task.viewAll");
    const canAssign = hasPermission("task.assign");
    const [ringTaskId, setRingTaskId] = useState("");
    // Page number in the URL path, filters (and the My / All tab) in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(LIST_STATE);
    const tab = filters.tab === "all" ? "all" : "mine"; // mine | all (all needs task.viewAll)
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hubs, setHubs] = useState([]);

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "GET", url: "/staff/hub/active" });
            setHubs(response?.success ? response?.data?.data || [] : []);
        })();
    }, []);

    const filterItems = useMemo(() => ({
        dropdown: [
            {
                key: "status",
                name: "Status",
                options: [
                    { key: "", name: "All" },
                    { key: "pending", name: "Pending" },
                    { key: "assigned", name: "Assigned" },
                    { key: "progress", name: "In progress" },
                    { key: "completed", name: "Completed" },
                    { key: "cancel", name: "Cancelled" },
                ],
            },
            {
                key: "taskType",
                name: "Type",
                options: [{ key: "", name: "All" }, { key: "Delivery", name: "Delivery" }, { key: "Pickup", name: "Pickup" }],
            },
            {
                key: "hubId",
                name: "Hub",
                options: [{ key: "", name: "All" }, ...hubs.map((h) => ({ key: h.hubId, name: [h.buildingNo, h.area, h.city].filter(Boolean).join(", ") || h.hubId }))],
            },
        ],
        daterangepicker: [
            { key: "createdRange", column: "createdAt", name: "Created between", from: { key: "from" }, to: { key: "to" } },
        ],
    }), [hubs]);

    const buildBody = useCallback(() => ({
        scope: canViewAll && tab === "all" ? "all" : "mine",
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.taskType ? { taskType: filters.taskType } : {}),
        ...(filters.hubId ? { hubId: filters.hubId } : {}),
        ...(filters.from ? { createdFrom: new Date(filters.from).toISOString() } : {}),
        ...(filters.to ? { createdTo: new Date(filters.to).toISOString() } : {}),
    }), [filters, tab, canViewAll]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({ method: "post", url: `/staff/task/list/${page - 1}`, params: { limit: filters.count }, data: buildBody() });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildBody, filters.count, page]);

    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };
    const changeTab = (next) => { setFilters((prev) => ({ ...prev, tab: next })); setPage(1); };
    const resetFilter = () => { setFilters({ ...INITIAL_FILTERS, tab }); setPage(1); };

    const columns = [
        { key: "taskId", name: "Task ID", mandatory: true },
        { key: "taskType", name: "Type" },
        { key: "status", name: "Status", render: (row) => <span className={`badge rounded-pill text-capitalize ${STATUS_BADGE[row.status] || "text-bg-secondary"}`}>{row.status}</span> },
        { key: "hubId", name: "Hub" },
        { key: "bookingId", name: "Booking", render: (row) => row.bookingId || <span className="text-muted">-</span> },
        ...(canViewAll && tab === "all" ? [{ key: "staffId", name: "Assigned to", render: (row) => row.staffId || <span className="text-muted">Unassigned</span> }] : []),
        { key: "taskTime", name: "Task time", render: (row) => formatDateTime(row.taskTime) },
        { key: "createdAt", name: "Created", render: (row) => formatDateTime(row.createdAt) },
        ...(canAssign ? [
            { key: "ring", name: "Ring", render: (row) => (["pending", "assigned"].includes(row.status) ? (
            <button type="button" className="btn btn-sm btn-outline-primary rn-row-btn" title={row.status === "pending" ? "Ring the staff who can take it" : "Ring the assignee to acknowledge"} onClick={(e) => { e.stopPropagation(); setRingTaskId(row.taskId); }}>Ring now</button>
        ) : null) },
        ] : []),
    ];

    return (
        <>
            {canViewAll ? (
                <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                        <button type="button" className={`nav-link ${tab === "mine" ? "active" : ""}`} onClick={() => changeTab("mine")}>My tasks</button>
                    </li>
                    <li className="nav-item">
                        <button type="button" className={`nav-link ${tab === "all" ? "active" : ""}`} onClick={() => changeTab("all")}>All tasks</button>
                    </li>
                </ul>
            ) : null}
            <ListLayout
                title="Tasks"
                description={canViewAll && tab === "all" ? "Every delivery and pickup task." : "Your delivery and pickup tasks."}
                data={data}
                loading={loading}
                error={error}
                onRetry={load}
                onRefresh={load}
                table={columns}
                page={page}
                setPageNo={setPage}
                pageSize={filters.count}
                onPageSizeChange={(size) => onFilterChange("count", size)}
                filters={filters}
                initialFilters={INITIAL_FILTERS}
                filterItems={filterItems}
                onFilterChange={onFilterChange}
                resetFilter={resetFilter}
                searchKeys={SEARCH_KEYS}
                searchPlaceholder="Search by task ID, booking, car or staff..."
                onRowClick={(row) => navigate(`/tasks/${row.taskId}`)}
            />
            {ringTaskId ? <RingNowDialog taskId={ringTaskId} statusUrl={`/staff/task/ring/${ringTaskId}`} ringUrl={`/staff/task/ring/${ringTaskId}`} onClose={() => setRingTaskId("")} onChanged={load} /> : null}
        </>
    );
};

export default TaskList;
